"use client";

import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { Card } from "@/components/new/card";
import OnlineUsers from "@/components/shared/online-users";
import { LinkedInIcon } from "@/icons";
import { cn, formatImageUrl, formatYoutubeLink, validateProductFormImageUrl } from "@/lib/utils";
import { TProduct, TProductReview, TRatingData } from "@/types/productv2";
import {
	BlocksIcon,
	Check,
	CheckIcon,
	ChevronDown,
	ChevronUp,
	EyeIcon,
	GlobeIcon,
	MapPinIcon,
} from "lucide-react";
import { Inter } from "next/font/google";
import Image from "next/image";
import Link from "next/link";
import { CreateAccountCard } from "../home/components/sidebar-cards";
import { ProductCarousel } from "../product-details/components/products-carousel";
import UpVoteButton from "@/components/common/upvote-button";
import ProductComments from "./components/comment/product-comment";
import FounderCard from "./components/founder-card";
import ProductRating from "./components/product-rating";
import FeaturedReviewCard from "./components/review/featured-review-card";
import ReviewCard from "./components/review/review-card";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from "@/components/new/carousel";
import ProductFAQCard from "../product-details/components/product-faq-card";
import { useState } from "react";
import ProfilePicture from "@/components/common/profile-picture";
import useIsMobile from "@/hooks/useIsMobile";
import { useModal } from "@/providers/modal-provider";
import ProductDetailsLoading from "../product-details/components/product-details-loading";
import { PRODUCTV2_API } from "@/lib/constants";
import { industriesServedData } from "../product-submission-v2/steps/product-information/components/industries-serve-data";
import { ProductTag } from "@/components/common/tag";
import { votesApi } from "@/frontend/service/votes/productv2-vote";
import { ModalType } from "@/types/modal-type";

const inter = Inter({ subsets: ["latin"] });

export const fetchReviews = async (productSlug: string) => {
	const res = await fetch(`${PRODUCTV2_API}/${productSlug}/reviews`);
	if (!res.ok) {
		const errorData = await res.json();
		throw new Error(errorData.message || "Failed to fetch reviews");
	}
	const data = await res.json();
	if (data?.success && Array.isArray(data?.data)) {
		return data.data;
	}
	throw new Error("Invalid response format");
};

export const fetchProductComments = async ({
	pageParam = 1,
	perPageParam = 10,
	productSlug,
}: {
	pageParam: number;
	perPageParam?: number;
	productSlug?: string;
}) => {
	const url = new URL(`${PRODUCTV2_API}/${productSlug}/comments`);
	url.searchParams.set("page", pageParam.toString());
	url.searchParams.set("perPage", perPageParam.toString());

	const res = await fetch(url.toString());
	if (!res.ok) throw new Error("Failed to load comments");

	const data = await res.json();
	return data;
};

export const fetchProductRating = async (productSlug: string): Promise<TRatingData> => {
	const url = new URL(`${PRODUCTV2_API}/${productSlug}/ratings`);
	const res = await fetch(url.toString(), {
		credentials: "include",
	});
	if (!res.ok) {
		throw new Error(`Failed to fetch rating: ${res.statusText}`);
	}
	const { data, success, message } = await res.json();
	if (!success) {
		throw new Error(message || "Failed to fetch rating data");
	}
	return data;
};

const ProductV2 = ({
	productSlug,
	product,
	isProductDetailsLoading = false,
}: {
	productSlug: string;
	product: TProduct;
	isProductDetailsLoading?: boolean;
}) => {
	const [showFAQ, setShowFAQ] = useState(false);
	const isMobile = useIsMobile();
	const { closeModal } = useModal();

	const { data: votesData, isLoading: votesLoading } = useQuery({
		queryKey: ["votes", productSlug],
		queryFn: () => votesApi.getProductVotesCount(productSlug),
		staleTime: 5 * 60 * 1000,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	const {
		data: reviews = [],
		isLoading: reviewsLoading,
		error: reviewsError,
	} = useQuery<TProductReview[], Error>({
		queryKey: ["reviews", productSlug],
		queryFn: () => fetchReviews(productSlug),
		staleTime: 5 * 60 * 1000,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	const {
		data: commentsData,
		isLoading: commentsLoading,
		isError: commentsError,
		fetchNextPage: fetchNextCommentsPage,
		hasNextPage: hasNextCommentsPage,
		isFetchingNextPage: isFetchingNextCommentsPage,
	} = useInfiniteQuery({
		queryKey: ["product-comments", productSlug],
		queryFn: ({ pageParam }) => fetchProductComments({ pageParam, productSlug }),
		getNextPageParam: (lastPage) => {
			const { page, totalPages } = lastPage?.pagination || {};
			return page && totalPages && page < totalPages ? page + 1 : undefined;
		},
		staleTime: 5 * 60 * 1000,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	const mergedComments = commentsData?.pages?.flatMap((page) => page?.data?.comments || []) || [];

	const { data: ratingData, isLoading: ratingLoading } = useQuery<TRatingData, Error>({
		queryKey: ["productRating", productSlug],
		queryFn: () => fetchProductRating(productSlug),
		enabled: !!productSlug,
		staleTime: 5 * 60 * 1000,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		placeholderData: {
			totalRating: "0",
			noOfRatings: 0,
			supportRating: "0",
			functionalityRating: "0",
			valueRating: "0",
			ratingCounts: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
		},
	});

	const maxUsers = isMobile ? 4 : 10;
	const displayedUsers = votesData?.data.usersWhoVoted
		? votesData.data.usersWhoVoted.slice(0, maxUsers)
		: [];
	const remainingVoters = votesData?.data.votesCount
		? Math.max(0, votesData.data.votesCount - displayedUsers.length)
		: 0;

	const youtubeDemoVideoUrl = formatYoutubeLink(product?.demoLink);
	const screenshots: string[] = [];
	product?.screenshots?.forEach((screenshot) => {
		const formattedImageUrl = formatImageUrl(screenshot);
		if (formattedImageUrl) {
			screenshots.push(formattedImageUrl);
		}
	});

	const productLogo = product?.logo || "";
	const isProductLogoValid = validateProductFormImageUrl(productLogo);

	const allTags = [...(product?.tags || []), ...(product?.customTags || [])];

	const industryIconMap = new Map(industriesServedData.map((item) => [item.title, item.iconPath]));

	const playstoreLink =
		product?.platformsAvailable && Array.isArray(product.platformsAvailable)
			? product.platformsAvailable.find((platform) => platform.android)?.android
			: undefined;

	const appstoreLink =
		product?.platformsAvailable && Array.isArray(product.platformsAvailable)
			? product.platformsAvailable.find((platform) => platform.ios)?.ios
			: undefined;

	if (
		isProductDetailsLoading ||
		votesLoading ||
		commentsLoading ||
		reviewsLoading ||
		ratingLoading
	) {
		return <ProductDetailsLoading />;
	}

	return (
		<div className="container mx-auto max-w-6xl p-6">
			<div className="grid gap-6 md:grid-cols-[1fr,auto]">
				{/* Header Section */}
				<div className="flex flex-col justify-between gap-4">
					<div className="flex items-start gap-4">
						<div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-lg border bg-gray-50">
							{isProductLogoValid ? (
								<Image
									src={formatImageUrl(productLogo)}
									alt={product?.name || ""}
									width={480}
									height={480}
									className="h-full w-full rounded-lg object-contain"
								/>
							) : (
								<BlocksIcon className="h-16 w-16 text-gray-600" />
							)}
						</div>

						<div className="space-y-2">
							<div className="space-y-2">
								<div className="flex items-center gap-2">
									<h1 className="text-3xl font-semibold">{product?.name}</h1>
									<Link href={product?.linkedIn || "#"} target="_blank">
										<LinkedInIcon />
									</Link>
								</div>
								<p className="text-muted-foreground">{product?.tagline}</p>
							</div>

							<div className="flex items-center gap-1 text-sm text-blue-600">
								{product?.hqLocation && (
									<div className="flex items-center gap-1 rounded-2xl bg-gray-100 p-2 text-xs leading-4 text-gray-600">
										<MapPinIcon className="h-3 w-3 text-blue-600" />
										{product.hqLocation}
									</div>
								)}
							</div>
							<div className="flex items-center gap-2 text-sm text-gray-600">
								{product?.viewsCount && (
									<div className="flex items-center gap-2">
										<EyeIcon className="h-5 w-5" />
										<p>{product?.viewsCount} Views</p>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="flex max-h-[8rem] flex-wrap gap-2 overflow-hidden">
						{/* Industries Served */}
						{product?.industriesServed?.length > 0 &&
							product.industriesServed.map((industryName, i) => {
								const iconPath = industryIconMap.get(industryName);
								return (
									<ProductTag
										key={`industry-${i}`}
										label={industryName}
										icon={iconPath}
										type="industries"
									/>
								);
							})}

						{/* Tags */}
						{allTags.length > 0 &&
							allTags.map((tag, i) => (
								<ProductTag key={`tag-${i}`} label={tag.name} slug={tag.name} type="tags" />
							))}
					</div>
				</div>

				{/* Rating & CTA Section */}
				<div className="space-y-4">
					<div className="h-22 flex gap-2">
						<UpVoteButton
							id={product?.id}
							name={product?.name}
							slug={product?.slug}
							upVotes={votesData?.data.score ?? 0}
							rating={ratingData?.totalRating ?? "0"}
							reviewsCount={reviews?.length ?? 0}
							isUpvoted={votesData?.data.isUpvotedByUser ?? false}
							keyToInvalidate={["votes", productSlug]}
						/>
					</div>

					<div className="h-full space-y-2">
						<Link
							href={product?.website ?? "#"}
							target="_blank"
							className="flex items-center justify-center rounded-lg bg-black p-2 text-white"
						>
							<GlobeIcon className="mr-2 h-4 w-4" />
							<span>Visit Website</span>
						</Link>
					</div>
				</div>
			</div>

			<div className="my-6 border-b"></div>

			<div className="my-6 space-y-4">
				<h2 className="text-xl font-semibold">Leaders</h2>
				<div className="mb-8 mt-6 flex items-center gap-4">
					{product?.team &&
						product?.team?.length > 0 &&
						product.team.map((member, i) => <FounderCard key={i} member={member} />)}
				</div>
			</div>
			{displayedUsers.length > 0 ? (
				<div className="my-6 flex items-center gap-2 border-b pb-6">
					<h2 className="text-xl font-semibold">Upvoted by :</h2>
					<span>
						<div className="flex items-center justify-start gap-x-1">
							<div className="flex items-center -space-x-3 px-4">
								{displayedUsers.map((userVote: any) =>
									userVote.isVerified ? (
										<Link
											key={userVote.id}
											href={userVote?.username ? `/${userVote.username}` : "#"}
											onClick={() => closeModal(ModalType.ProductDetails)}
										>
											<ProfilePicture
												profilePictureUrl={formatImageUrl(userVote?.image) || undefined}
												alternateText={userVote?.name}
												isVerified={userVote?.isVerified}
												isFounder={userVote?.isFounder}
												className={{
													container: "h-10 w-10 rounded-full bg-white",
													image: "rounded-full text-gray-600",
													badgeImage: "-bottom-4 h-8 w-8",
												}}
											/>
										</Link>
									) : null
								)}
							</div>
							<p className="text-left text-lg font-semibold text-gray-800">
								<span className="text-xs text-brand-primary md:text-base">
									{remainingVoters > 0 ? ` +${remainingVoters} others` : ""}
								</span>
							</p>
						</div>
					</span>
				</div>
			) : null}

			{/* Screenshots Grid */}
			<div className="my-6 space-y-4">
				<h2 className="text-xl font-semibold">Screenshots</h2>
				<div>
					<ProductCarousel
						video={youtubeDemoVideoUrl}
						images={screenshots}
						productSlug={product?.slug}
					/>
				</div>
			</div>

			{/* Description */}
			<div className="my-8 space-y-4">
				<h2 className="text-xl font-semibold">Description</h2>
				<p className="text-muted-foreground">
					<div
						className={cn(
							inter.className,
							"w-3/4 whitespace-pre-wrap break-words text-sm text-gray-600"
						)}
					>
						{product?.description}
					</div>
				</p>
			</div>

			<div className="my-8 border-b"></div>

			<div className="flex justify-between gap-16">
				<div className="w-full lg:w-4/5">
					<div className="grid gap-4 md:grid-cols-3">
						<Card className="space-y-4 bg-gray-100 p-4">
							<div className="flex items-center justify-between gap-4">
								<h3 className="font-medium">Targeted Customers</h3>
								<div className="h-5 w-5">
									<Image
										className="h-full w-full object-contain"
										src="/images/product-submission-form-v2/product-preview/targeted-customers.svg"
										width={16}
										height={16}
										alt="icon"
									/>
								</div>
							</div>
							<div className="w-full border-b-2"></div>
							<div className="space-y-2">
								{(product?.targetCustomers || []).slice(0, 4).map((item) => (
									<div key={item} className="flex items-center gap-2 text-sm">
										<Check className="h-4 w-4" />
										{item}
									</div>
								))}
								{product?.targetCustomers?.length > 4 && (
									<div className="text-sm font-semibold text-gray-600">+10 more</div>
								)}
							</div>
						</Card>

						<Card className="space-y-4 bg-gray-100 p-4">
							<div className="flex items-center justify-between gap-4">
								<h3 className="font-medium">Operating Region</h3>
								<div className="h-4 w-4">
									<Image
										className="h-full w-full object-contain"
										src="/images/product-submission-form-v2/product-preview/operating-region.svg"
										width={16}
										height={16}
										alt="icon"
									/>
								</div>
							</div>
							<div className="w-full border-b-2"></div>
							<div className="space-y-2">
								{(product?.regionsOfOperation || []).slice(0, 4).map((item) => (
									<div key={item} className="flex items-center gap-2 text-sm">
										<Check className="h-4 w-4" />
										{item}
									</div>
								))}
								{product?.regionsOfOperation?.length > 4 && (
									<div className="text-sm font-semibold text-gray-600">+10 more</div>
								)}
							</div>
						</Card>

						<Card className="space-y-4 bg-gray-100 p-4">
							<div className="flex items-center justify-between gap-4">
								<h3 className="font-medium">Product Offering</h3>
								<div className="h-5 w-5">
									<Image
										className="h-full w-full object-contain"
										src="/images/product-submission-form-v2/product-preview/product-offering.svg"
										width={16}
										height={16}
										alt="icon"
									/>
								</div>
							</div>
							<div className="w-full border-b-2"></div>
							<div className="space-y-2">
								{(product?.offerings || []).slice(0, 4).map((item) => (
									<div key={item} className="flex items-center gap-2 text-sm">
										<Check className="h-4 w-4" />
										{item}
									</div>
								))}
								{product?.offerings?.length > 4 && (
									<div className="text-sm font-semibold text-gray-600">+10 more</div>
								)}
							</div>
						</Card>
					</div>
				</div>

				{playstoreLink || appstoreLink ? (
					<div className="w-full space-y-2 lg:w-1/5">
						<h3 className="font-medium">Platforms Available</h3>
						<div className="flex flex-col gap-2">
							{appstoreLink && (
								<Link href={appstoreLink} className="hover:opacity-80" target="_blank">
									<Image
										src="/images/product-submission-form-v2/platforms/appstore.svg"
										alt="Download on the App Store"
										width={140}
										height={42}
										className="w-full max-w-[144px]"
									/>
								</Link>
							)}
							{playstoreLink && (
								<Link href={playstoreLink} className="hover:opacity-80" target="_blank">
									<Image
										src="/images/product-submission-form-v2/platforms/playstore.svg"
										alt="Get it on Google Play"
										width={140}
										height={42}
										className="w-full max-w-[144px]"
									/>
								</Link>
							)}
						</div>
					</div>
				) : null}
			</div>

			<div className="mt-8 flex items-center justify-center gap-4">
				<div className="w-full space-y-4">
					<h2 className="mb-8 text-xl font-semibold">Features</h2>
					{product?.features && product?.features?.length > 0 ? (
						<div className="grid items-center gap-4 md:grid-cols-2">
							{product.features.map((feature, i) => (
								<div key={i} className="flex items-center gap-2">
									<div className="rounded-full bg-blue-500 p-0.5">
										<CheckIcon className="h-3 w-3 rounded-full text-white" />
									</div>
									<span className="text-sm text-gray-600">{feature}</span>
								</div>
							))}
						</div>
					) : null}
				</div>
			</div>

			{/* Product FAQ Section */}
			{product?.faqs &&
				(product.faqs.solution ||
					product.faqs.problemsSolving ||
					product.faqs.differentitation ||
					product.faqs.milestones) && (
					<div className="my-6 space-y-6">
						<div className="flex items-center gap-4">
							<h2 className="text-xl font-semibold text-gray-800">More Product Details</h2>
							<button
								onClick={() => setShowFAQ((prev) => !prev)}
								className="rounded-md p-2 text-gray-700 hover:bg-gray-100"
								aria-label={showFAQ ? "Collapse FAQ" : "Expand FAQ"}
							>
								{showFAQ ? (
									<ChevronUp className="h-6 w-6 stroke-[2.5]" />
								) : (
									<ChevronDown className="h-6 w-6 stroke-[2.5]" />
								)}
							</button>
						</div>
						{showFAQ && (
							<div className="animate-fade-in-down">
								<div>
									{product?.faqs?.solution && (
										<ProductFAQCard
											question="What problem does your product solve?"
											answer={product?.faqs?.solution || "-"}
										/>
									)}
									{product?.faqs?.problemsSolving && (
										<ProductFAQCard
											question="How does your product solve it?"
											answer={product?.faqs?.problemsSolving || "-"}
										/>
									)}
									{product?.faqs?.differentitation && (
										<ProductFAQCard
											question="What sets your product apart from competitors?"
											answer={product?.faqs?.differentitation || "-"}
										/>
									)}
									{product?.faqs?.milestones && (
										<ProductFAQCard
											question="What has been your biggest product accomplishment/milestones?"
											answer={product?.faqs?.milestones || "-"}
										/>
									)}
								</div>
							</div>
						)}
					</div>
				)}

			<div className="my-6 border-b"></div>

			{/* Featured Reviews Section */}
			{product?.reviews?.length > 0 ? (
				<div className="my-6 space-y-6 border-b pb-6">
					<h2 className="text-xl font-semibold text-gray-800">Featured Reviews</h2>
					<div className="relative mx-auto max-w-4xl">
						<Carousel opts={{ align: "start", loop: true }} className="w-full">
							<CarouselContent>
								{product.reviews.map((review, i) => (
									<CarouselItem key={i} className="flex justify-center">
										<FeaturedReviewCard review={review} />
									</CarouselItem>
								))}
							</CarouselContent>
							{product.reviews.length > 1 && (
								<>
									<CarouselPrevious
										className="absolute -left-14 top-1/2 h-10 w-10 -translate-y-1/2 rounded-full bg-white shadow-md hover:bg-gray-50"
										variant="outline"
										size="icon"
									/>
									<CarouselNext
										className="absolute -right-14 top-1/2 h-10 w-10 -translate-y-1/2 rounded-full bg-white shadow-md hover:bg-gray-50"
										variant="outline"
										size="icon"
									/>
								</>
							)}
						</Carousel>
					</div>
				</div>
			) : null}

			{/* Product Rating Section */}
			<div className="my-8">
				{product && (
					<ProductRating
						product={product}
						ratingData={ratingData}
						productSlug={productSlug}
						reviews={reviews}
					/>
				)}
			</div>

			<div className="mb-6 flex gap-6 border-b text-lg font-semibold text-gray-800">
				<button
					onClick={() => {
						document.getElementById("comments")?.scrollIntoView({ behavior: "smooth" });
					}}
					className="relative pb-2 transition hover:text-black hover:after:absolute hover:after:bottom-0 hover:after:left-0 hover:after:h-0.5 hover:after:w-full hover:after:bg-black"
				>
					Comments
				</button>
				<button
					onClick={() => {
						document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" });
					}}
					className="relative pb-2 transition hover:text-black hover:after:absolute hover:after:bottom-0 hover:after:left-0 hover:after:h-0.5 hover:after:w-full hover:after:bg-black"
				>
					Reviews
				</button>
			</div>

			{/* Comments and Reviews Section */}
			<div className="flex flex-wrap items-start justify-between gap-8">
				<div className="w-full space-y-6 lg:max-w-[calc(100%-20rem)]">
					<section id="comments" className="space-y-4">
						<h2 className="text-xl font-semibold text-gray-800">Comments</h2>
						<div className="space-y-4">
							<ProductComments
								productSlug={productSlug}
								comments={mergedComments}
								isError={commentsError}
								fetchNextPage={fetchNextCommentsPage}
								hasNextPage={hasNextCommentsPage}
								isFetchingNextPage={isFetchingNextCommentsPage}
							/>
						</div>
					</section>

					<section id="reviews" className="space-y-4">
						<h2 className="text-xl font-semibold text-gray-800">Reviews</h2>
						<div className="space-y-4">
							<ReviewCard reviews={reviews} error={reviewsError} productSlug={productSlug} />
						</div>
					</section>
				</div>

				<aside className="w-full space-y-5 lg:w-72">
					<OnlineUsers />
					<CreateAccountCard />
				</aside>
			</div>
		</div>
	);
};

export default ProductV2;
